import React, { useContext, useLayoutEffect } from 'react';
// @ts-ignore
import { DateTime } from 'luxon';
import { HistoricalComparisonFilterDialog } from './HistoricalComparisonFilterDialog';
// @ts-ignore
import { EnsureAccount } from 'components/customer/accounts/EnsureAccount';
import "components/readings/CustomerSummary.scss";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useUser } from 'components/auth/PermissionApiEndpoint';
import { UcLoading } from 'components/common/UcLoading';
import IconButton from '@mui/material/IconButton';
import { UcWidget } from '../common/UcWidget';
import { filterTypes, serviceTypes } from '../../utils/Enums';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
// @ts-ignore
import { UserInfoContext } from '../../lib/UserInfoProvider';
// @ts-ignore
import { useMyAccounts } from '../customer/accounts/MyAccountAPIEndpoints';
// @ts-ignore
import { dateRangeFormatterV2, uomToCommodity, commodityToUom } from '../../lib/Formatters';
import { getAccountPopoverTitle, AccountContext, AccountContextType } from '../../lib/AccountProvider';
import { Box } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// @ts-ignore
import { useResponsive } from 'lib/useResponsive';
// @ts-ignore
import { UcHint } from 'components/common/UcHint';
import { useLocation } from "react-router-dom";
import { isMeterInRange } from 'utils/helpers/general.helpers';
import { HistoricalComparisonChart } from './HistoricalComparisonChart';
import { getDateRangesForMeterCheck } from './HistoricalComparisonChartUtils';
// @ts-ignore
import { PageContext, PageState, PageAction } from 'lib/PageProvider';
import EnsureUserAndModule from '../auth/EnsureUserAndModule';
import { useMyTimezone } from 'components/customer/orgs/MyOrgAPIEndpoints';
import { UsageHelpers } from '../usage/MyUsage.helpers';
import { UcMeterSelectModular } from '../common/UcMeterSelectModular';
import { USAGE_FILTER_TYPES } from './MyUsage.constants';
import { UcButtonHistoricalChartDownload } from './UcButtonHistoricalChartDownload';

export const HistoricalComparisonPage = () => {
    const [pageState, pageDispatch] = useContext<[PageState, React.Dispatch<PageAction>]>(PageContext);
    const { t } = useTranslation();
    const timezone = useMyTimezone();
    const { isMobile, isTablet } = useResponsive();
    const [open, setOpen] = React.useState<boolean>(false);
    const location = useLocation();
    const user = useUser();
    const { account } = React.useContext<AccountContextType>(AccountContext);
    const selectedAccount = account;
    const showEST = user.isImpersonating;

    const swrMyAccounts = useMyAccounts();
    const [selectedMeter, setSelectedMeter] = React.useState("All")

    const monthlyHeader = t("Historical Usage Comparison");
    const monthlyDescription = t("This chart displays a month-to-month usage comparison of your selected commodity over a range of billing periods.");
    const dailyHeader = t("Daily Usage Comparison");
    const dailyDescription = t("This chart displays a day-to-day usage comparison of your selected commodity over a range of days.");

    const [chartType, setChartType] = React.useState<string>('month');
    const [uom, setUom] = React.useState<string>(location.state?.locUom ?? 'KWH');
    const [geoCoordinates, setGeoCoordinates] = React.useState(UsageHelpers.getGeoCoordinates(account, uom) ?? []);

    const [chartHeader, setChartHeader] = React.useState<string>(monthlyHeader);
    const [chartDescription, setChartDescription] = React.useState<string>(monthlyDescription);

    const [csvDataToDownload, setCsvDataToDownload] = React.useState([]);

    const today = DateTime.now();

    const [dailyStart, setDailyStart] = React.useState(today.minus({ years: 1, days: 15 }));
    const [dailyEnd, setDailyEnd] = React.useState(today);

    const [monthlyStart, setMonthlyStart] = React.useState(today.minus({ years: 1 }).startOf('year'));
    const [monthlyEnd, setMonthlyEnd] = React.useState(today);

    const [startFilter, setStartFilter] = React.useState(monthlyStart);
    const [endFilter, setEndFilter] = React.useState(monthlyEnd);

    const [yearOne, setYearOne] = React.useState(today.minus({ years: 1 }));
    const [yearTwo, setYearTwo] = React.useState(today);

    const [dateRangeStart, setDateRangeStart] = React.useState<any>(UsageHelpers.createDateFromObject(today.year, today.month, 1, timezone, 'yearStart'));
    const [dateRangeEnd, setDateRangeEnd] = React.useState<any>(UsageHelpers.createDateFromObject(today.year, today.month, today.day, timezone, 'hourEnd'));

    const handleChangeChartType = (event: any) => {
        const type = event.target.value;

        if (type === filterTypes.MONTH) {
            setChartHeader(monthlyHeader);
            setChartDescription(monthlyDescription);

            setStartFilter(monthlyStart);
            setEndFilter(monthlyEnd);
        }
        else if (type === filterTypes.DAY) {
            setChartHeader(dailyHeader);
            setChartDescription(dailyDescription);

            setStartFilter(dailyStart);
            setEndFilter(dailyEnd);
        }

        setChartType(type);
    };

    const handleChangeGeoCoordinates = (newUom: string) => {
        if (account?.metersJson) {
            setGeoCoordinates(JSON.parse(account?.metersJson)
                .filter((meter: any) =>
                    meter.latitude !== undefined
                    && meter.longitude !== undefined
                    && meter.meterCommodity === uomToCommodity(newUom)
                ).map((meter: any) => {
                    return {
                        latitude: meter.latitude,
                        longitude: meter.longitude
                    }
                }));
        }
    }

    const createDateRanges = (values: any) => {
        const { yearOne, yearTwo, startMonth, endMonth, startDate, endDate } = values;

        setYearOne(yearOne);
        setYearTwo(yearTwo);

        const minYear = Math.min(yearOne.year, yearTwo.year);
        const maxYear = Math.max(yearOne.year, yearTwo.year);

        if (chartType === "month") {
            setStartFilter(DateTime.fromObject({ year: minYear, month: startMonth.month, day: 1 }, { zone: timezone }));
            setEndFilter(DateTime.fromObject({ year: maxYear, month: endMonth.month, day: 1 }, { zone: timezone }));

            setMonthlyStart(DateTime.fromObject({ year: minYear, month: startMonth.month, day: 1 }, { zone: timezone }));
            setMonthlyEnd(DateTime.fromObject({ year: maxYear, month: endMonth.month, day: 1 }, { zone: timezone }));
        }
        else if (chartType === "day") {
            setStartFilter(DateTime.fromObject({ year: startDate.year, month: startDate.month, day: startDate.day }, { zone: timezone }));
            setEndFilter(DateTime.fromObject({ year: endDate.year, month: endDate.month, day: endDate.day }, { zone: timezone }));

            setDailyStart(DateTime.fromObject({ year: startDate.year, month: startDate.month, day: startDate.day }, { zone: timezone }));
            setDailyEnd(DateTime.fromObject({ year: endDate.year, month: endDate.month, day: endDate.day }, { zone: timezone }));
        }    
    }

    let alertText = "";
    if (isMobile) {
        alertText = t("Tap a bar to get more info about it. \nSwipe left or right on the graph to reveal more dates if they're available.");
    }
    else {
        alertText = t("Click on a bar to get more info about it.");
    }

    let defaultValues = {
        yearOne: yearOne,
        yearTwo: yearTwo,
        startMonth: startFilter,
        endMonth: endFilter,
        startDate: dailyStart,
        endDate: dailyEnd
    }

    const handleOpen = () => {
        setOpen(true);
    }

    const onChangeOpen = (isOpen: boolean) => {
        setOpen(isOpen);
    }

    useLayoutEffect(() => {
        pageDispatch({
            header: t("Historical Usage Comparison"),
            actionItems: [] as any[],
            variableHeader: "",
            tabs: [],
            onTabChange: () => { },
            tabValue: ''
        });
    }, [pageState.header, pageDispatch, t]);

    useLayoutEffect(() => {      
        if (account?.meterServices && account?.meterServices?.length > 0) {
            let activeMetersForUom = (JSON.parse(account?.metersJson || '{}')
                .filter((item: any) => item.meterCommodity === uomToCommodity(uom) && isMeterInRange(item, startFilter, endFilter)));

            if (activeMetersForUom.length < 1) {
                let activeMeters = (JSON.parse(account?.metersJson || '{}')
                    .filter((item: any) => isMeterInRange(item, startFilter, endFilter)));

                if (activeMeters.length > 0) {
                    setUom(commodityToUom(activeMeters[0].meterCommodity));
                    setSelectedMeter(activeMeters[0].serialNo);
                }
            }
        }
    }, [account?.meterServices, account?.metersJson, endFilter, startFilter, swrMyAccounts, uom]);

    const handleChangeSelectedMeter = (newOption: any) => {
        const uom = commodityToUom(newOption?.meterCommodity);
        setUom(uom);
        handleChangeGeoCoordinates(uom);

        if (newOption.serialNo && !newOption?.serialNo.includes("All")) {
            setSelectedMeter(newOption.serialNo);
        }
        else {
            setSelectedMeter("All");
        }
    };


    const getFileName = () => {
        const formattedCurrentDate = DateTime.now().toFormat("yyyyMMdd");

        switch (chartType) {
            case USAGE_FILTER_TYPES.MONTH:
                return `monthlyHistoricalUsage_${account?.extAccountNumber}_${yearOne.year}_${yearTwo.year}_${formattedCurrentDate}.csv`;
            case USAGE_FILTER_TYPES.DAY:
                return `dailyHistoricalUsage_${account?.extAccountNumber}_${yearOne.year}_${yearTwo.year}_${formattedCurrentDate}.csv`;
            default:
                return `historicalUsage_${account?.extAccountNumber}_${yearOne.year}_${yearTwo.year}_${formattedCurrentDate}.csv`;
        }
    }

    const actionItems = [
        {
            component: (
                <UcMeterSelectModular
                    startDate={getDateRangesForMeterCheck(startFilter, endFilter, yearOne.year, yearTwo.year, timezone)?.rangeOne.start ?? startFilter}
                    endDate={getDateRangesForMeterCheck(startFilter, endFilter, yearOne.year, yearTwo.year, timezone)?.rangeTwo.end ?? endFilter}
                    uom={uom}
                    onlyCommodity={false}
                    forcedWidth={(!isMobile && !isTablet) ? '60%' : '100%'}
                    onChange={handleChangeSelectedMeter}
                    marginTop={"0.5rem"}
                />
            )
        },
        {
            component: (
                <ToggleButtonGroup
                    value={chartType}
                    size="small"
                    exclusive
                    onChange={handleChangeChartType}
                    aria-label="ChartType"
                    sx={{pt: 1, pl: !isMobile ? 0.5 : 0}}
                >
                    <ToggleButton color='primary' value="month" data-cy-id="ucDateToggle-month">{t('Month')}</ToggleButton>
                    <ToggleButton color='primary' value="day" data-cy-id="ucDateToggle-day">{t('Day')}</ToggleButton>
                </ToggleButtonGroup>
            )
        },
        {
            component: (
                <Box/> //used to push commodity icons to next row
            )
        },
        {
            component: (
                <IconButton
                    title={t('Date Filter')}
                    data-cy-id="historicalComparison-dateFilterButton"
                    onClick={handleOpen}
                    aria-label="filter"
                    sx={{
                        color: 'primary.main'
                    }}
                >
                    <CalendarMonthIcon />
                </IconButton>
            )
        },      
        {
            component: (
                <UcHint
                    hintText={chartDescription}
                    alertText={alertText}
                />
            )
        },
    ];

    if (csvDataToDownload.length > 0) {
        const chartDownloadComponent = {
            component: (
                <UcButtonHistoricalChartDownload
                    data={csvDataToDownload}
                    selectedMeter={selectedMeter}
                    accountNumber={account?.extAccountNumber}
                    filename={getFileName()}
                    palletColor='primary.main'
                    yearOne={yearOne.year}
                    yearTwo={yearTwo.year}
                    chartType={chartType}
                    uom={uom}
                />
            )
        };

        actionItems.push(chartDownloadComponent);
    }

    const { userInfo } = React.useContext<any>(UserInfoContext);
    const popoverTitle = getAccountPopoverTitle(selectedAccount);

    return (
        <EnsureAccount>
            <EnsureUserAndModule modules={['DataWarehouse']}>
                <UcLoading isLoading={swrMyAccounts.isLoading} hasFade={false}>
                    <HistoricalComparisonFilterDialog
                        open={open}
                        onChangeOpen={onChangeOpen}
                        filterTypes={filterTypes}
                        serviceTypes={serviceTypes}
                        chartType={chartType}
                        defaultValues={defaultValues}
                        username={userInfo.username}
                        onSubmit={(values: any) => {
                            createDateRanges(values);
                        }}
                    />
                    <UcWidget
                        actionItems={actionItems}
                        title={chartHeader + " - " + selectedAccount?.accountAddress}
                        subTitle={dateRangeFormatterV2(dateRangeStart, dateRangeEnd, timezone)}
                        popoverTitle={popoverTitle}
                        contentPadding={'0 0 1rem 0'}
                        alignTitleLeft={true}
                        height={!isMobile ? "850px" : "1000px"}
                    >
                        <UcLoading isLoading={selectedAccount === null} hasFade={false}>
                            <HistoricalComparisonChart
                                chartType={chartType}
                                showEST={showEST}
                                geoCoordinates={geoCoordinates}
                                uom={uom}
                                accountId={selectedAccount?.id}
                                yearOne={yearOne.year}
                                yearTwo={yearTwo.year}
                                accountNumber={selectedAccount?.extAccountNumber}
                                startDate={startFilter}
                                endDate={endFilter}
                                selectedMeter={selectedMeter}
                                setDateRangeStart={setDateRangeStart}
                                setDateRangeEnd={setDateRangeEnd}
                                setCsvDataToDownload={setCsvDataToDownload}
                            />
                        </UcLoading>
                    </UcWidget>
                </UcLoading>
            </EnsureUserAndModule>       
        </EnsureAccount>
    );
};